import { fromZonedTime } from 'date-fns-tz';
import { haversineMeters } from './distance';

const LIMA_TZ = 'America/Lima';
const MS_PER_DAY = 86_400_000;

export interface FlagContext { kind: 'ingreso' | 'salida'; deviceTs: string; serverTs: string;
  positionDate: string; startTime: string; endTime: string;
  lat: number | null; lng: number | null; pdvLat: number | null; pdvLng: number | null;
  lateToleranceMin: number; pdvRadiusM: number; clockSkewMin: number;
  /** Hora del ingreso YA marcado de ESTA misma asignación; null si todavía no hay.
   *  Sin ingreso no hay con qué medir cuánto trabajó: la bandera no se marca. */
  entryDeviceTs: string | null;
  /** Porcentaje del turno por debajo del cual la jornada cuenta como medio día
   *  (`app_settings.half_day_threshold_pct`, hoy 75). */
  halfDayThresholdPct: number; }

/** Cómo se lee cada bandera en pantalla. Única fuente de la traducción. */
export const FLAG_LABEL: Record<string, string> = {
  tardanza: 'Tardanza',
  salida_temprana: 'Salida antes de tiempo',
  sin_gps: 'Sin GPS',
  lejos_del_pdv: 'Lejos del PDV',
  hora_sospechosa: 'Hora sospechosa',
  /**
   * La pone SOLO el servidor (0314): la foto de esta marca es copia exacta de la de otra marca.
   * No sale de `computeFlags` porque el celular no puede saberlo: hace falta ver las demás fotos.
   * Validación la acompaña con cuál es la otra (`domain/fotoRepetida`).
   */
  foto_repetida: 'Foto repetida: es la misma que otra marca',
};

/** Normaliza 'HH:mm' → 'HH:mm:ss' (la BD entrega time como 'HH:mm:ss'). */
function normalizeTime(t: string): string {
  return t.length === 5 ? `${t}:00` : t;
}

/** Duración PROGRAMADA del turno en minutos, interpretando las horas en America/Lima.
 *  Si la hora de fin no es posterior a la de inicio se entiende que el turno cruza la
 *  medianoche (22:00 a 06:00 son 8 h, no −16 h). */
export function shiftMinutes(positionDate: string, startTime: string, endTime: string): number {
  const start = fromZonedTime(`${positionDate}T${normalizeTime(startTime)}`, LIMA_TZ).getTime();
  let end = fromZonedTime(`${positionDate}T${normalizeTime(endTime)}`, LIMA_TZ).getTime();
  if (end <= start) end += MS_PER_DAY;
  return (end - start) / 60_000;
}

export interface EarlyExitInput {
  positionDate: string;
  startTime: string;
  endTime: string;
  entryDeviceTs: string | null;
  exitDeviceTs: string;
  halfDayThresholdPct: number;
}

/**
 * Fracción del turno efectivamente trabajada (1 = turno entero), o `null` cuando no se
 * puede calcular: sin ingreso marcado, con la salida antes del ingreso o con un turno de
 * duración cero. `null` NO es "trabajó poco": es "no se sabe", y no debe castigar a nadie.
 */
export function workedShiftFraction(i: EarlyExitInput): number | null {
  if (!i.entryDeviceTs) return null;
  const workedMs = new Date(i.exitDeviceTs).getTime() - new Date(i.entryDeviceTs).getTime();
  if (!Number.isFinite(workedMs) || workedMs <= 0) return null;
  const shiftMs = shiftMinutes(i.positionDate, i.startTime, i.endTime) * 60_000;
  if (!(shiftMs > 0)) return null;
  return workedMs / shiftMs;
}

/**
 * La regla del medio día (acordada con el jefe de operaciones): se compara el tiempo
 * EFECTIVAMENTE trabajado (salida − ingreso) contra la duración PROGRAMADA del turno.
 * Menos del umbral → salió antes de tiempo y ese día cuenta 0.5 en el cierre.
 * Exactamente el umbral cuenta día completo: la regla es "menos del umbral".
 *
 * Que una tardanza no descuente es coherente con esto: llegar 2 h tarde a un turno de
 * 9 h y salir a la hora deja 7/9 = 78% → día completo. Salir 4 h antes deja 56% → medio.
 */
export function isEarlyExit(i: EarlyExitInput): boolean {
  const fraction = workedShiftFraction(i);
  if (fraction === null) return false;
  return fraction * 100 < i.halfDayThresholdPct;
}

/** Flags automáticos de una asistencia. Orden estable:
 *  tardanza → salida_temprana → sin_gps → lejos_del_pdv → hora_sospechosa.
 *  `pdvRadiusM` es el radio de ESA tienda (pdv.radius_m), no el default global.
 *  El servidor recalcula todo esto en `attendance_flags_servidor`: lo de aquí es
 *  feedback inmediato para el recurso, no la fuente de verdad. */
export function computeFlags(ctx: FlagContext): string[] {
  const flags: string[] = [];
  const deviceMs = new Date(ctx.deviceTs).getTime();

  // tardanza: solo ingreso, contra positionDate+startTime interpretados en America/Lima
  if (ctx.kind === 'ingreso') {
    const scheduledStart = fromZonedTime(
      `${ctx.positionDate}T${normalizeTime(ctx.startTime)}`,
      LIMA_TZ,
    );
    const limitMs = scheduledStart.getTime() + ctx.lateToleranceMin * 60_000;
    if (deviceMs > limitMs) flags.push('tardanza');
  }

  // salida_temprana: solo salida, y solo si ya hay un ingreso con el que comparar
  if (
    ctx.kind === 'salida' &&
    isEarlyExit({
      positionDate: ctx.positionDate,
      startTime: ctx.startTime,
      endTime: ctx.endTime,
      entryDeviceTs: ctx.entryDeviceTs,
      exitDeviceTs: ctx.deviceTs,
      halfDayThresholdPct: ctx.halfDayThresholdPct,
    })
  ) {
    flags.push('salida_temprana');
  }

  // sin_gps / lejos_del_pdv (lejos solo se evalúa con GPS y coordenadas de PDV presentes)
  if (ctx.lat === null || ctx.lng === null) {
    flags.push('sin_gps');
  } else if (ctx.pdvLat !== null && ctx.pdvLng !== null) {
    const d = haversineMeters(ctx.lat, ctx.lng, ctx.pdvLat, ctx.pdvLng);
    if (d > ctx.pdvRadiusM) flags.push('lejos_del_pdv');
  }

  // hora_sospechosa: divergencia absoluta dispositivo/servidor mayor al umbral
  const skewMs = Math.abs(deviceMs - new Date(ctx.serverTs).getTime());
  if (skewMs > ctx.clockSkewMin * 60_000) flags.push('hora_sospechosa');

  return flags;
}
